import React, { useState, useRef, useEffect } from "react";
import { useMediaUpload } from "../hooks/useMediaUpload";
import { useAuth } from "../../../context/AuthContext";

interface FileUploaderProps {
  customerId: string;
  isLogo?: boolean;
  mediaType?: "processed" | "raw";
  tags?: string[];
  additionalMetadata?: Record<string, any>;
  onUploadComplete?: (cloudFrontUrl: string, s3Key: string) => void;
  onError?: (error: string) => void;
  onUploadingChange?: (isUploading: boolean) => void;
  acceptedFileTypes?: string;
  buttonLabel?: string;
  className?: string;
  hidePreview?: boolean;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  customerId,
  isLogo = false,
  mediaType = "processed",
  tags = [],
  additionalMetadata = {},
  onUploadComplete,
  onError,
  onUploadingChange,
  acceptedFileTypes = "image/*,video/*",
  buttonLabel = "Upload File",
  className = "",
  hidePreview = false,
}) => {
  const { idToken } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const {
    upload,
    isUploading,
    uploadProgress,
    uploadError,
    uploadedUrl,
    resetUpload,
  } = useMediaUpload({ onUploadComplete, onError });

  useEffect(() => {
    if (onUploadingChange) {
      onUploadingChange(isUploading);
    }
  }, [isUploading, onUploadingChange]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);

    if (file) {
      handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    if (!idToken) {
      if (onError) onError("Authentication token missing");
      return;
    }

    await upload({
      file,
      customerId,
      isLogo,
      mediaType,
      tags,
      additionalMetadata,
      idToken,
    });
  };

  const handleButtonClick = () => {
    resetUpload();
    fileInputRef.current?.click();
  };

  return (
    <div className={`file-uploader ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept={acceptedFileTypes}
        className="hidden"
        disabled={isUploading}
      />

      <button
        type="button"
        onClick={handleButtonClick}
        disabled={isUploading}
        className={`px-4 py-2 rounded-md text-white ${
          isUploading ? "bg-gray-400" : "bg-brand-primary hover:bg-brand-primary-hover"
        }`}
      >
        {isUploading ? "Uploading..." : buttonLabel}
      </button>

      {selectedFile && (
        <div className="mt-2 text-sm">
          <p>Selected file: {selectedFile.name}</p>
        </div>
      )}

      {isUploading && (
        <div className="mt-2">
          <div className="h-2 w-full bg-surface-ui rounded-full">
            <div
              className="h-full bg-brand-primary rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <p className="text-sm text-center mt-1">{uploadProgress}%</p>
        </div>
      )}

      {uploadError && (
        <div className="mt-2 text-red-500 text-sm">{uploadError}</div>
      )}

      {uploadedUrl && !isUploading && (
        <div className="mt-2 text-green-600 text-sm">
          <p>Upload successful!</p>
          {!hidePreview && (uploadedUrl.endsWith(".jpg") ||
          uploadedUrl.endsWith(".jpeg") ||
          uploadedUrl.endsWith(".png") ||
          uploadedUrl.endsWith(".gif")) ? (
            <img
              src={uploadedUrl}
              alt="Uploaded file preview"
              className="mt-2 max-h-40 max-w-full rounded-md border border-gray-200"
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

export default FileUploader;
